import { z } from 'zod';

import { isValidBannerUrl } from '../utils/bannerUrl';

/**
 * Destino do clique: opcional, mas quando preenchido tem de ser uma URL que o app
 * consiga abrir (ver {@link isValidBannerUrl}). A mensagem é a chave i18n que a
 * tela usa para explicar a recusa.
 */
const bannerUrlSchema = z.string().trim().refine(isValidBannerUrl, 'urlInvalid');

/** Idiomas em que o conteúdo do banner pode ser cadastrado. */
export const BANNER_LANGUAGES = {
  PtBr: 'pt-BR',
  EnUs: 'en-US',
  EsEs: 'es-ES',
} as const;

/** Idioma selecionado na barra de idioma do formulário. */
export type BannerLanguage = (typeof BANNER_LANGUAGES)[keyof typeof BANNER_LANGUAGES];

/** Idiomas de tradução (tudo menos o português, que é a fonte de verdade). */
export const BANNER_TRANSLATION_LANGUAGES = [BANNER_LANGUAGES.EnUs, BANNER_LANGUAGES.EsEs] as const;

/** Idioma de tradução (chave do mapa `translations`). */
export type BannerTranslationLanguage = (typeof BANNER_TRANSLATION_LANGUAGES)[number];

/**
 * Posição enviada por padrão no payload do banner.
 *
 * O campo **Posição** foi descontinuado da tela (a US o removeu), mas o contrato
 * continua aceitando `Position` — então o formulário envia um valor fixo (`Top`)
 * em vez de omitir, para o backend nunca receber o banner sem posição. Na edição,
 * a posição que já estava salva é preservada.
 */
export const BANNER_DEFAULT_POSITION = 1;

/** Tipo de mídia do banner — imagem ou vídeo, nunca os dois. */
export const BANNER_MEDIA_KIND = { Image: 'image', Video: 'video' } as const;

/** Tipo de mídia selecionado no bloco Mídia. */
export type BannerMediaKind = (typeof BANNER_MEDIA_KIND)[keyof typeof BANNER_MEDIA_KIND];

/** Turma vinculada, exibida na seção "Turmas" do formulário. */
const classStudentSchema = z.object({
  classStudentId: z.number(),
  /** Descrição de exibição da turma (não é enviada ao salvar — só o ID). */
  description: z.string(),
  /** Marca da turma, quando o lookup a informa; só para exibição. */
  brandName: z.string().optional(),
  /**
   * Marca que vinculou esta turma **em bloco** (filtro de marca).
   *
   * Ausente nas turmas adicionadas uma a uma pelo autocomplete — é isso que
   * permite desmarcar uma marca sem desfazer o que foi escolhido à mão.
   */
  brandId: z.number().optional(),
});

/**
 * Conteúdo de um idioma de tradução (título não entra: a API não persiste).
 *
 * A mídia acompanha o tipo escolhido no banner: com imagem o idioma sobe uma
 * arte, com vídeo sobe um MP4 — nunca os dois, como no cadastro em português.
 */
const translationSchema = z.object({
  url: bannerUrlSchema,
  /** Arte nova escolhida para este idioma; `null` = mantém a que está lá. */
  imageFile: z.instanceof(File).nullable(),
  /** Arte do par legado deste idioma (`Image` no contrato). */
  imageDesktopFile: z.instanceof(File).nullable(),
  /** MP4 novo escolhido para este idioma; `null` = mantém o que está lá. */
  videoFile: z.instanceof(File).nullable(),
  /** ID do registro de idioma já existente (`undefined` = tradução nova). */
  langId: z.number().optional(),
  /** Arte já cadastrada no servidor para este idioma (par mobile). */
  existingImageUrl: z.string().optional(),
  /** Arte já cadastrada no servidor para este idioma (par legado). */
  existingImageDesktopUrl: z.string().optional(),
  /** Vídeo já cadastrado no servidor para este idioma. */
  existingVideoUrl: z.string().optional(),
});

/**
 * Schema do formulário de Banner (criar/editar), validado por Zod via React
 * Hook Form.
 *
 * **Não** derivado de `postApiV1BannerMutationRequestSchema` (gerado pelo Kubb):
 * aquele schema é exportado com o cast `as unknown as ToZod<...>`, o que torna a
 * shape do `ZodObject` uma ficção de tipo — `.pick()`/`.extend()` sobre ele
 * degradam o `z.infer` para `never` (mesma decisão já registrada em
 * `series-management/types/serieForm.ts` e `location/types/countryForm.ts`).
 * O contrato de escrita também não tem o mesmo shape do formulário: é
 * PascalCase, multipart, e o mapeamento vive em `useSaveBanner`.
 */
export const bannerFormSchema = z
  .object({
    companyId: z.number().optional(),
    title: z.string().trim().min(1),
    /** Data em `YYYY-MM-DD` (formato do `DatePicker`); `''` quando vazia. */
    startDate: z.string(),
    endDate: z.string(),
    /** Valor de `EBannerPriority` escolhido no select. */
    priority: z.number().int(),
    /** Posição do banner — sem campo em tela; ver {@link BANNER_DEFAULT_POSITION}. */
    position: z.number().int(),
    url: bannerUrlSchema,
    /** Tempo em tela, em segundos. Travado pela duração do vídeo em modo vídeo. */
    duration: z.number().int().min(1),
    showTitle: z.boolean(),
    general: z.boolean(),
    active: z.boolean(),
    isMonograph: z.boolean(),
    isMonitor: z.boolean(),
    classStudents: z.array(classStudentSchema),
    mediaKind: z.enum([BANNER_MEDIA_KIND.Image, BANNER_MEDIA_KIND.Video]),
    /**
     * Arte nova escolhida (350 × 270) para o par **mobile** — a que o app exibe e o
     * preview espelha. `null` = mantém a existente.
     */
    imageFile: z.instanceof(File).nullable(),
    /** Arte já cadastrada no servidor (par mobile), exibida no preview na edição. */
    existingImageUrl: z.string().optional(),
    /**
     * Arte do par **legado** (`Image` no contrato), consumida só pelo ambiente
     * antigo. Não aparece no preview.
     */
    imageDesktopFile: z.instanceof(File).nullable(),
    /** Arte já cadastrada no servidor para o par legado. */
    existingImageDesktopUrl: z.string().optional(),
    /** MP4 escolhido no modo vídeo; enviado em `Video` ao salvar. */
    videoFile: z.instanceof(File).nullable(),
    /** Vídeo já cadastrado no servidor (o cadastro devolve o caminho em `filePathMobile`). */
    existingVideoUrl: z.string().optional(),
    /** Duração lida do MP4, em segundos; alimenta `duration` e a trava do campo. */
    videoDurationSeconds: z.number().nullable(),
    /**
     * Tipo de mídia que **está no cadastro** (`null` = nenhuma).
     *
     * Vem da leitura do banner e é atualizado a cada save. Como o cadastro não
     * apaga mídia, é isso que diz se trocar o tipo exige um arquivo novo.
     */
    savedMediaKind: z.enum([BANNER_MEDIA_KIND.Image, BANNER_MEDIA_KIND.Video]).nullable(),
    translations: z.object({
      [BANNER_LANGUAGES.EnUs]: translationSchema,
      [BANNER_LANGUAGES.EsEs]: translationSchema,
    }),
  })
  .superRefine((values, ctx) => {
    if (!values.general && values.classStudents.length === 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['classStudents'],
        message: 'classStudentsRequired',
      });
    }

    // Período inválido acusa nos dois campos: quem errou pode ter sido qualquer um
    // dos dois, e o operador corrige onde faz mais sentido para ele.
    if (values.startDate && values.endDate && values.endDate < values.startDate) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['startDate'],
        message: 'startDateAfterEnd',
      });
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['endDate'],
        message: 'endDateBeforeStart',
      });
    }

    // Tradução é complemento, não substituto: o app cai no cadastro em português
    // quando o idioma do aluno não tem conteúdo. Preencher só o inglês/espanhol
    // publicaria um banner sem título ou sem arte para todo mundo, então o
    // salvamento fica barrado até o português estar completo.
    const hasFilledTranslation = BANNER_TRANSLATION_LANGUAGES.some(culture =>
      isTranslationFilled(values.translations[culture]),
    );

    if (hasFilledTranslation && bannerPortugueseGaps(values).length > 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['translations'],
        message: 'portugueseFirst',
      });
    }

    // O cadastro não remove mídia — só substitui: sem arquivo enviado, o que está lá
    // continua lá. Então trocar o tipo exige escolher o arquivo novo; salvar sem ele
    // deixaria o banner exibindo a mídia do tipo antigo, sem jeito de corrigir.
    const isVideo = values.mediaKind === BANNER_MEDIA_KIND.Video;
    const switchedKind =
      values.savedMediaKind !== null && values.savedMediaKind !== values.mediaKind;

    if (switchedKind && isVideo && values.videoFile === null) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['videoFile'],
        message: 'videoRequiredToReplaceImage',
      });
    }

    if (switchedKind && !isVideo && values.imageFile === null) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['imageFile'],
        message: 'imageRequiredToReplaceVideo',
      });
    }

    // A arte vai para dois destinos: `ImageMobile` (MBX) e `Image` (ambiente
    // legado). Enviar só a do MBX deixaria o legado exibindo a arte antiga — e o
    // cadastro não apaga mídia —, então a nova do MBX exige a do legado. Um par
    // legado já cadastrado satisfaz: a edição pode estar trocando só o do MBX.
    if (
      !isVideo &&
      values.imageFile !== null &&
      values.imageDesktopFile === null &&
      values.existingImageDesktopUrl === undefined
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['imageDesktopFile'],
        message: 'imageDesktopRequired',
      });
    }
  });

/** Valores do formulário de Banner. */
export type BannerFormValues = z.infer<typeof bannerFormSchema>;

/** Conteúdo de um idioma de tradução dentro do formulário. */
export type BannerTranslationFormValue =
  BannerFormValues['translations'][BannerTranslationLanguage];

/** Turma vinculada dentro do formulário. */
export type BannerClassStudentFormValue = BannerFormValues['classStudents'][number];

/** Tradução vazia — estado inicial de uma aba de idioma sem conteúdo. */
export const EMPTY_BANNER_TRANSLATION: BannerTranslationFormValue = {
  url: '',
  imageFile: null,
  imageDesktopFile: null,
  videoFile: null,
};

/**
 * Valores iniciais da criação: `Ativo` e `Exibir título` ligados, mídia em
 * imagem, prioridade média (3 em `EBannerPriority`) e 5 segundos em tela.
 */
export const INITIAL_BANNER_FORM_VALUES: BannerFormValues = {
  companyId: undefined,
  title: '',
  startDate: '',
  endDate: '',
  priority: 3,
  position: BANNER_DEFAULT_POSITION,
  url: '',
  duration: 5,
  showTitle: true,
  general: true,
  active: true,
  isMonograph: false,
  isMonitor: false,
  classStudents: [],
  mediaKind: BANNER_MEDIA_KIND.Image,
  imageFile: null,
  imageDesktopFile: null,
  videoFile: null,
  videoDurationSeconds: null,
  savedMediaKind: null,
  translations: {
    [BANNER_LANGUAGES.EnUs]: EMPTY_BANNER_TRANSLATION,
    [BANNER_LANGUAGES.EsEs]: EMPTY_BANNER_TRANSLATION,
  },
};

/**
 * Parte do cadastro em português que ainda falta para as traduções poderem
 * existir: o título (que a API só persiste em português) ou a mídia.
 */
export type BannerPortugueseGap = 'title' | 'media';

/** `true` quando o banner tem mídia em português — arquivo escolhido ou já cadastrada. */
function hasPortugueseMedia(values: BannerFormValues): boolean {
  if (values.mediaKind === BANNER_MEDIA_KIND.Video) {
    return values.videoFile !== null || values.existingVideoUrl !== undefined;
  }

  return (
    values.imageFile !== null ||
    values.imageDesktopFile !== null ||
    values.existingImageUrl !== undefined ||
    values.existingImageDesktopUrl !== undefined
  );
}

/**
 * O que falta no cadastro em português para as traduções poderem ser salvas.
 *
 * Vazio quando o português está completo. A mídia conta por tipo escolhido: em
 * modo vídeo, o MP4; em modo imagem, qualquer um dos dois pares de arte (o do
 * MBX ou o legado) — nenhum dos dois é obrigatório sozinho.
 *
 * @param values - Valores atuais do formulário.
 * @returns Lacunas em ordem de leitura da tela.
 *
 * @example
 * ```ts
 * const gaps = bannerPortugueseGaps(values); // ['title'] → falta o título
 * ```
 */
export function bannerPortugueseGaps(values: BannerFormValues): readonly BannerPortugueseGap[] {
  const gaps: BannerPortugueseGap[] = [];

  if (values.title.trim() === '') gaps.push('title');
  if (!hasPortugueseMedia(values)) gaps.push('media');

  return gaps;
}

/** Uma tradução conta como preenchida quando tem url ou mídia (imagem ou vídeo). */
export function isTranslationFilled(translation: BannerTranslationFormValue): boolean {
  return (
    translation.url.trim() !== '' ||
    translation.imageFile !== null ||
    translation.imageDesktopFile !== null ||
    translation.videoFile !== null ||
    translation.existingImageUrl !== undefined ||
    translation.existingImageDesktopUrl !== undefined ||
    translation.existingVideoUrl !== undefined
  );
}
